import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { InternalOrderEnums } from 'src/app/enum/internal-order-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InternalOrderDetailTableModel } from 'src/app/models/shopping/internal-order-detail-table.model';
import { InternalOrderTableModel } from 'src/app/models/shopping/internal-order-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { InternalOrderDetailService } from 'src/app/services/shopping/internal-order-detail.service';
import { InternalOrderService } from 'src/app/services/shopping/internal-order.service';

@Component({
  selector: 'app-internal-order',
  templateUrl: './internal-order.component.html',
  styleUrls: ['./internal-order.component.scss'],
  standalone: false
})
export class InternalOrderComponent implements OnInit {
  generalHeader: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<InternalOrderTableModel> = new PagedResponseModel<InternalOrderTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  detailData: PagedResponseModel<InternalOrderDetailTableModel> = new PagedResponseModel<InternalOrderDetailTableModel>();
  nameWindow: string = 'Consulta Pedidos Internos';
  projectName = ProjectName.projectName;

  activeTabIndex = 0;
  page: number = 1;
  limit: number = 10;
  columnSort: string | null = null;
  ascendant: boolean = true;
  filters: ColumnResponseModel[] = []
  previousSort: { field: string; isAscsendant: number };
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  constructor(private internalOrderService: InternalOrderService,
    private internalOrderDetailService: InternalOrderDetailService,
    private router: Router,
    private title: Title,
    private headerService: HeaderService,
    private toastr: ToastrService) { }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(this.nameWindow + ' - ' + this.projectName);
    const filters = localStorage.getItem('internal-order');
    this.filters = filters ? JSON.parse(filters) : [];
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllInternalOrder(),
    ]);
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllInternalOrder()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllInternalOrderDetail()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllInternalOrder();
        break;
      case 1:
        await this.getAllInternalOrderDetail();
        break;
    }
  }

  async onExport() {

    const response = await firstValueFrom(
      this.internalOrderService.getPermission(InternalOrderEnums.exportExcel)
    );
    if (!response) {
      this.toastr.error('El usuario no tiene permisos para exportar a excel pedidos internos. Comuníquese con el administrador del sistema.', 'Error.', {
        timeOut: 5000, progressBar: true, closeButton: true
      });
      return;
    }

    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.internalOrderService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Pedidos Internos.xlsx'; // El nombre del archivo
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los pedidos internos.', error);
    }
  }

  async exportDetailConsultation() {
    try {

      const blob = await firstValueFrom(this.internalOrderDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Pedidos Internos Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los pedidos internos detalle.', error);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);

    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.internalOrderService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getAllInternalOrder() {
    try {
      this.generalData = await firstValueFrom(this.internalOrderService.getAllInternalOrder(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los pedidos internos.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.internalOrderDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getAllInternalOrderDetail() {
    try {
      this.detailData = await firstValueFrom(this.internalOrderDetailService.getAllInternalOrderDetails(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los detalles de los pedidos internos.', error);
    }
  }

  newInternalOrder() {
    this.router.navigate(['/internal-order/register']);
  }

  onAction(event: { action: string, item?: InternalOrderTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/internal-order/register/${event.item.internalOrderId}`]);
    }
  }
}
